// Escritura de un fotograma del héroe en el DOM. El reloj la llama hasta 60 veces por segundo, y un
// render de React por fotograma con ~260 registros no cabe en el presupuesto (RESEARCH §Q10): por eso
// se escribe directamente en los nodos que pintó el servidor, sin crear ninguno y sin pasar por el estado de React.
// Sin hex: el azul de los halos sale de --brand-600-rgb y la sombra dura de --shadow-color.
import type { EstiloNodo, FotogramaHeroe, FotogramaTexto } from './escenaHeroe';

type Nodo = HTMLElement | SVGElement;

export function sombraFuncion(halo: number): string {
    return `0 0 0 ${halo}px rgb(var(--brand-600-rgb) / .28), 3px 3px 0 0 var(--shadow-color)`;
}

export function sombraCita(halo: number): string {
    return `0 0 0 ${halo}px rgb(var(--brand-600-rgb) / .25)`;
}

export interface NodosEscena {
    readonly registros: Nodo[];
    readonly ondas: Nodo[];
    readonly contador: Nodo[];
    readonly funciones: Nodo[];
    readonly etiquetasFuncion: Nodo[];
    readonly sectores: Nodo[];
    readonly arcos: Nodo[];
    readonly hilos: Nodo[];
    readonly tramo: Nodo[];
    readonly etiquetas: Nodo[];
    readonly compositor: Nodo[];
    readonly envio: Nodo[];
    readonly vacio: Nodo[];
    readonly burbuja: Nodo[];
    readonly pensando: Nodo[];
    readonly pensandoTexto: Nodo[];
    readonly pulso: Nodo[];
    readonly avatar: Nodo[];
    readonly respuestas: Nodo[];
    readonly cita: Nodo[];
    readonly chipCita: Nodo[];
    readonly notaFija: Nodo[];
    readonly paquetes: Nodo[];
}

const SELECTORES: Record<keyof NodosEscena, string> = {
    registros: '[data-registro]',
    ondas: '[data-onda]',
    contador: '[data-contador]',
    funciones: '[data-funcion]',
    etiquetasFuncion: '[data-etiqueta-funcion]',
    sectores: '[data-sector]',
    arcos: '[data-arco]',
    hilos: '[data-hilo]',
    tramo: '[data-tramo]',
    etiquetas: '[data-etiqueta]',
    compositor: '[data-compositor]',
    envio: '[data-envio]',
    vacio: '[data-vacio]',
    burbuja: '[data-burbuja]',
    pensando: '[data-pensando]',
    pensandoTexto: '[data-pensando-texto]',
    pulso: '[data-pulso]',
    avatar: '[data-avatar]',
    respuestas: '[data-respuesta]',
    cita: '[data-cita]',
    chipCita: '[data-chip-cita]',
    notaFija: '[data-nota-fija]',
    paquetes: '[data-paquete]',
};

/** Los nodos de una composición, una sola vez al arrancar el reloj, en el orden en que se pintaron. */
export function recogerNodos(raiz: Element): NodosEscena {
    const entradas = Object.entries(SELECTORES).map(([clave, selector]) => [clave, [...raiz.querySelectorAll<Nodo>(selector)]]);
    return Object.fromEntries(entradas) as NodosEscena;
}

type Propiedad = 'opacity' | 'transform' | 'width' | 'height' | 'boxShadow' | 'transformOrigin' | 'display';

// Lo último escrito en cada nodo. No se compara con `style`, porque el navegador normaliza lo que
// guarda (espacios, decimales) y la comparación fallaría siempre.
const ESCRITO = new WeakMap<Nodo, Partial<Record<Propiedad, string>>>();

// Solo se toca el DOM si el valor cambia: la mayoría de los registros están quietos casi todo el bucle.
function poner(nodo: Nodo | undefined, propiedad: Propiedad, valor: string) {
    if (!nodo) return;
    let escrito = ESCRITO.get(nodo);
    if (!escrito) ESCRITO.set(nodo, (escrito = {}));
    if (escrito[propiedad] === valor) return;
    nodo.style[propiedad] = valor;
    escrito[propiedad] = valor;
}

function ponerTexto(nodo: Nodo | undefined, texto: string) {
    if (nodo && nodo.textContent !== texto) nodo.textContent = texto;
}

function ponerDato(nodo: Nodo | undefined, clave: string, valor: string | null) {
    if (!nodo || nodo.dataset[clave] === (valor ?? undefined)) return;
    if (valor === null) delete nodo.dataset[clave];
    else nodo.dataset[clave] = valor;
}

function ponerEstilo(nodo: Nodo | undefined, estilo: EstiloNodo) {
    poner(nodo, 'opacity', String(estilo.opacidad));
    poner(nodo, 'transform', estilo.transformacion);
}

function escribirRegistros(nodos: NodosEscena, f: FotogramaHeroe) {
    f.registros.forEach((r, i) => {
        const nodo = nodos.registros[i];
        ponerEstilo(nodo, r);
        poner(nodo, 'width', `${r.ancho}px`);
        poner(nodo, 'height', `${r.alto}px`);
        ponerDato(nodo, 'luz', r.luz);
    });
}

function escribirFunciones(nodos: NodosEscena, f: FotogramaHeroe) {
    f.funciones.forEach((fn, j) => {
        const activa = String(fn.activa);
        ponerDato(nodos.funciones[j], 'activa', activa);
        ponerDato(nodos.sectores[j], 'activa', activa);
        ponerDato(nodos.arcos[j], 'activa', activa);
        poner(nodos.funciones[j], 'boxShadow', sombraFuncion(fn.halo));
        poner(nodos.etiquetasFuncion[j], 'opacity', String(fn.etiqueta));
    });
}

function escribirTextos(lista: Nodo[], textos: readonly FotogramaTexto[]) {
    textos.forEach((e, i) => {
        ponerEstilo(lista[i], e);
        ponerTexto(lista[i], e.texto);
    });
}

function escribirTrazos(nodos: NodosEscena, f: FotogramaHeroe) {
    f.ondas.forEach((o, i) => ponerEstilo(nodos.ondas[i], o));
    f.hilos.forEach((h, i) => {
        ponerEstilo(nodos.hilos[i], h);
        poner(nodos.hilos[i], 'width', `${h.ancho}px`);
    });
    ponerEstilo(nodos.tramo[0], f.tramo);
    poner(nodos.tramo[0], 'transformOrigin', f.tramo.origen);
    escribirTextos(nodos.etiquetas, f.etiquetas);
    f.paquetes.forEach((p, i) => ponerEstilo(nodos.paquetes[i], p));
}

function escribirConversacion(nodos: NodosEscena, f: FotogramaHeroe) {
    ponerTexto(nodos.compositor[0], f.compositor.texto);
    ponerDato(nodos.compositor[0], 'escribiendo', String(f.compositor.escribiendo));
    poner(nodos.envio[0], 'transform', f.compositor.escalaEnvio);
    poner(nodos.vacio[0], 'opacity', String(f.vacio));
    escribirTextos(nodos.burbuja, [f.burbuja]);
    poner(nodos.pensando[0], 'opacity', String(f.pensando.opacidad));
    ponerTexto(nodos.pensandoTexto[0], f.pensando.texto);
    poner(nodos.pulso[0], 'transform', f.pensando.pulso);
    poner(nodos.avatar[0], 'opacity', String(f.avatar));
}

function escribirRespuestas(nodos: NodosEscena, f: FotogramaHeroe) {
    f.respuestas.forEach((r, m) => {
        ponerEstilo(nodos.respuestas[m], r);
        poner(nodos.respuestas[m], 'display', r.visible ? 'flex' : 'none');
    });
    poner(nodos.cita[0], 'boxShadow', sombraCita(f.haloCita));
    poner(nodos.chipCita[0], 'opacity', String(f.chipCita));
    ponerEstilo(nodos.notaFija[0], f.notaFija);
}

/** Escribe el fotograma `f` en los nodos de una composición. */
export function aplicarEscena(nodos: NodosEscena, f: FotogramaHeroe): void {
    escribirRegistros(nodos, f);
    escribirFunciones(nodos, f);
    escribirTrazos(nodos, f);
    escribirConversacion(nodos, f);
    escribirRespuestas(nodos, f);
    ponerTexto(nodos.contador[0], f.contador);
}
